/**
 * Tabs — cream-themed wrappers over Radix Tabs. Two list styles: "underline"
 * (default — a rose underline under the active trigger) and "pill" (a sunken
 * track with a raised active pill). Compose:
 *
 *   <Tabs defaultValue="a">
 *     <TabsList variant="pill">
 *       <TabsTrigger value="a">角色</TabsTrigger>
 *       <TabsTrigger value="b">场景</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="a">…</TabsContent>
 *     <TabsContent value="b">…</TabsContent>
 *   </Tabs>
 */
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import * as RadixTabs from "@radix-ui/react-tabs";

export const Tabs = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixTabs.Root>
>(function Tabs({ className, ...rest }, ref) {
  return (
    <RadixTabs.Root
      ref={ref}
      className={["lc-tabs", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});

export interface TabsListProps
  extends ComponentPropsWithoutRef<typeof RadixTabs.List> {
  /** "underline" (default) or "pill". */
  readonly variant?: "underline" | "pill";
}

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
  { variant = "underline", className, ...rest },
  ref,
) {
  const classes = [
    "lc-tabs__list",
    variant === "pill" ? "lc-tabs__list--pill" : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <RadixTabs.List ref={ref} className={classes} {...rest} />;
});

export const TabsTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof RadixTabs.Trigger>
>(function TabsTrigger({ className, ...rest }, ref) {
  return (
    <RadixTabs.Trigger
      ref={ref}
      className={["lc-tabs__trigger", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});

export const TabsContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(function TabsContent({ className, ...rest }, ref) {
  return (
    <RadixTabs.Content
      ref={ref}
      className={["lc-tabs__content", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});
